import { Parser } from '~/api';
import { Server } from '~/api/models/Server';
import Logger from '~/core/logger';
import RequestService from './request';

export enum ServerStatus {
    Loading = 'loading',
    Error = 'error',

    Offline = 'offline',
    Online = 'running',
    Starting = 'starting',
    Stopping = 'stopping',

    Migrating = 'migrating',

    Installing = 'installing',
    FailedInstall = 'failed_install',

    Suspended = 'suspended',
    Updating = 'updating',
    Moving = 'moving',

    CreatingBackup = 'creating_backup',
    DeployingBackup = 'deploying_backup',
}

export const mappedState: { [status: string]: string } = {
    [ServerStatus.Loading]: 'loading',
    [ServerStatus.Error]: 'danger',

    [ServerStatus.Offline]: 'danger',
    [ServerStatus.Online]: 'success',
    [ServerStatus.Starting]: 'info',
    [ServerStatus.Stopping]: 'warning',

    [ServerStatus.Migrating]: 'warning',

    [ServerStatus.Installing]: 'warning',
    [ServerStatus.FailedInstall]: 'danger',

    [ServerStatus.Suspended]: 'warning',
    [ServerStatus.Updating]: 'info',
    [ServerStatus.Moving]: 'info',

    [ServerStatus.CreatingBackup]: 'info',
    [ServerStatus.DeployingBackup]: 'info',
};

export interface ServerStatsProcess {
    cpu_used: number;
    memory_used: number;
    disk_used: number;
    network: {
        rx_bytes: number;
        tx_bytes: number;
    };
}

export interface ServerStats {
    status: ServerStatus;
    process?: ServerStatsProcess;
    query?: Record<string, any>; // TODO: Me
}

interface GetStatsRequest {
    servers: string[];
}

class ServersService {
    getAll(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/servers', {
            ...req,
            sort: req.sort ?? 'position',
            include: ['node', 'egg', 'allocations', 'allocations.subdomain'],
        }).then(Parser.parse);
    }

    get(): Promise<Server> {
        return RequestService.get('/servers/:server', {
            include: [
                'node',
                'node.location',
                'nest',
                'egg',
                'allocations',
                'features',
                'allocations.subdomain',
                'user',
                'transfer.oldNode',
                'transfer.newNode',
                'transfer.newAllocations',
            ],
        }).then(Parser.parse);
    }

    favorite(server: Server): Promise<void> {
        return RequestService.post(`/servers/${server.getRouteID()}/favorite`);
    }

    unfavorite(server: Server): Promise<void> {
        return RequestService.delete(`/servers/${server.getRouteID()}/favorite`);
    }

    private stats(data: GetStatsRequest): Promise<{ [uuid: string]: ServerStats }> {
        return RequestService.get('/servers/stats', data);
    }

    /**
     * One-shot stats fetch. Used by callers that want their own poll
     * cadence (e.g. the bulk-shutdown modal, which polls every 1s)
     * instead of the shared 15s `registerStats` queue. Pass
     * `bustCache: true` to force a fresh daemon read (the per-node
     * cache TTL drops to 1s for that hit).
     *
     * `bustCache` is wire-encoded as 1/0 instead of true/false because
     * Laravel's `boolean` validator rejects the strings "true"/"false"
     * that JS produces when serializing booleans to a query string.
     */
    statsFresh(data: GetStatsRequest & { bustCache?: boolean }): Promise<{ [uuid: string]: ServerStats }> {
        const { bustCache, ...rest } = data;
        return RequestService.get('/servers/stats', {
            ...rest,
            ...(bustCache !== undefined ? { bustCache: bustCache ? 1 : 0 } : {}),
        });
    }

    private queue: { [uuid: string]: (stats: ServerStats) => void } = {};
    private startupTimer: null | ReturnType<typeof setTimeout> = null;
    private fetchTimer: null | ReturnType<typeof setInterval> = null;

    constructor() {
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    private handleVisibilityChange = (): void => {
        if (Object.keys(this.queue).length < 1) return;

        if (document.hidden) {
            this.stopPolling();
        } else {
            this.startPolling();
        }
    };

    registerStats(serverUuid: string, callback: (stats: ServerStats) => void) {
        Logger.debug('ServersService', `Registering ${serverUuid} for stats...`);
        this.queue[serverUuid] = callback;
        this.startPolling();
    }

    private startPolling(): void {
        // Already polling, or the tab is hidden: in the latter case the
        // visibilitychange handler restarts us once it becomes visible again.
        if (this.fetchTimer || document.hidden) return;

        // Due to how this logic is handled inside ServerCard, we could have
        // multiple server cards register itself in a short period of time.
        //
        // To avoid overcomplicating things, we will have a 10ms window for all items
        // to register itself before we start fetching.
        if (this.startupTimer) {
            clearTimeout(this.startupTimer);
            this.startupTimer = null;
        }

        this.startupTimer = setTimeout(() => {
            this.startupTimer = null;
            this.fetchStats();
            this.fetchTimer = setInterval(this.fetchStats.bind(this), 15000);
        }, 10);
    }

    private stopPolling(): void {
        if (this.startupTimer) {
            clearTimeout(this.startupTimer);
            this.startupTimer = null;
        }

        if (this.fetchTimer) {
            clearInterval(this.fetchTimer);
            this.fetchTimer = null;
        }
    }

    fetchStats() {
        Logger.debug('ServersService', 'Fetching all server stats...');

        const fetchedServer: { [uuid: string]: boolean } = {};

        this.stats({
            servers: Object.keys(this.queue),
        })
            .then((res) => {
                for (const uuid in res) {
                    if (!this.queue[uuid]) {
                        Logger.warn('ServersService', `Received stats for ${uuid} even though it wasn't requested...?`);
                        continue;
                    }

                    this.queue[uuid](res[uuid]);
                    fetchedServer[uuid] = true;
                }
            })
            .catch((err) => {
                Logger.warn('ServersService', `Failed fetching server stats (${Object.keys(this.queue).join(', ')}): ${err}`);
            })
            .finally(() => {
                // If either the request failed or is missing some specific
                // server for whatever reason, their stats should be set to
                // an equivalent of an error.
                for (const uuid of Object.keys(this.queue)) {
                    if (fetchedServer[uuid] || !this.queue[uuid]) continue;

                    Logger.debug('ServersService', `Missing data for ${uuid}, assuming it's dead.`);
                    this.queue[uuid]({
                        status: ServerStatus.Error,
                    });
                }
            });
    }

    unregisterStats(serverUuid: string) {
        Logger.debug('ServersService', `Unregistering ${serverUuid} from stats...`);
        delete this.queue[serverUuid];

        if (Object.keys(this.queue).length < 1) {
            this.stopPolling();
        }
    }
}

export default new ServersService();
